
import { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Language,
  getTranslations,
  translate,
  DEFAULT_LANGUAGE,
  detectBrowserLanguage,
  getLanguageFromPath,
  addLanguageToPath,
  removeLanguageFromPath
} from "@/i18n"
import { getGameByLocalizedId, getLocalizedGameId, isGameAvailableInLanguage } from "@/lib/games-data"

// Map language codes to HTML lang attributes
const languageToHtmlLang: Record<Language, string> = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR"
}

// Static files that should not be intercepted by the router
const STATIC_FILES = [
  'ads.txt',
  'robots.txt',
  'sitemap.xml',
  'favicon.ico',
  'manifest.json'
]

// Helper function to check if a path is a static file
const isStaticFile = (pathname: string): boolean => {
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1]

  return STATIC_FILES.some(staticFile =>
    lastSegment === staticFile || pathname.endsWith(staticFile)
  )
}

interface TranslationContextType {
  language: Language
  setLanguage: (language: Language) => void
  t: (key: string, params?: Record<string, any>) => string
  navigate: (path: string) => void
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

export const useTranslation = () => {
  const context = useContext(TranslationContext)
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider')
  }
  return context
}

interface TranslationProviderProps {
  children: ReactNode
}

export function TranslationProvider({ children }: TranslationProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE)
  const [initialized, setInitialized] = useState(false)

  // Initialize language from URL or detection
  useEffect(() => {
    // Skip language detection for static files
    if (isStaticFile(location.pathname)) {
      setInitialized(true)
      return
    }

    const urlLanguage = getLanguageFromPath(location.pathname)

    if (urlLanguage) {
      // Language is in URL, use it
      setLanguageState(urlLanguage)
      setInitialized(true)
    } else {
      // No language in URL, detect and redirect
      const detectedLanguage = detectBrowserLanguage()
      const currentPath = location.pathname + location.search + location.hash
      const newPath = addLanguageToPath(currentPath, detectedLanguage)

      navigate(newPath, { replace: true })
    }
  }, [location.pathname, navigate])

  // Update language when URL changes
  useEffect(() => {
    if (!initialized) return

    // Skip language detection for static files
    if (isStaticFile(location.pathname)) {
      return
    }

    const urlLanguage = getLanguageFromPath(location.pathname)
    if (urlLanguage && urlLanguage !== language) {
      setLanguageState(urlLanguage)
    }
  }, [location.pathname, language, initialized])

  // Update HTML lang attribute when language changes
  useEffect(() => {
    const htmlLang = languageToHtmlLang[language] || languageToHtmlLang[DEFAULT_LANGUAGE]
    document.documentElement.lang = htmlLang
  }, [language])

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Navigation function that preserves language context
  const localizedNavigate = (path: string) => {
    // Skip adding language for static files
    if (isStaticFile(path)) {
      navigate(path)
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo(0, 0), 0)
      return
    }

    // Check if path already has language
    const hasLanguage = getLanguageFromPath(path)
    if (hasLanguage) {
      navigate(path)
    } else {
      const localizedPath = addLanguageToPath(path, language)
      navigate(localizedPath)
    }

    // Scroll to top after navigation
    setTimeout(() => window.scrollTo(0, 0), 0)
  }

  const setLanguageWithNavigation = (newLanguage: Language) => {
    // Skip language change for static files
    if (isStaticFile(location.pathname)) {
      return
    }

    if (newLanguage === language) return

    let newPath: string

    // Get current path without the language prefix
    const currentPath = location.pathname + location.search + location.hash

    // Parse the path to detect game pages
    const pathSegments = location.pathname.split('/').filter(Boolean)

    // Buscar el índice del segmento "game"
    const gameSegmentIndex = pathSegments.findIndex(segment => segment === 'game')
    const gameIdSegmentIndex = gameSegmentIndex + 1

    if (pathSegments[gameSegmentIndex] === 'game' && pathSegments[gameIdSegmentIndex]) {
      // We're on a game page, convert the localized game ID
      const currentLocalizedGameId = pathSegments[gameIdSegmentIndex]
      const currentGame = getGameByLocalizedId(currentLocalizedGameId, language)

      if (currentGame) {
        // Verificar si el juego está disponible en el nuevo idioma
        if (!isGameAvailableInLanguage(currentGame.id, newLanguage)) {
          // El juego no está disponible en el nuevo idioma, redirigir a home
          newPath = addLanguageToPath('/', newLanguage)
        } else {
          // Get the localized ID for the new language
          const newLocalizedGameId = getLocalizedGameId(currentGame.id, newLanguage)

          // Rebuild the path with the new language and new localized game ID
          const pathWithoutLang = removeLanguageFromPath(currentPath)
          const updatedPath = pathWithoutLang.replace(
            `/game/${currentLocalizedGameId}`,
            `/game/${newLocalizedGameId}`
          )
          newPath = addLanguageToPath(updatedPath + location.search + location.hash, newLanguage)
        }
      } else {
        // Fallback to normal language switching if game not found
        const currentPath = removeLanguageFromPath(location.pathname + location.search + location.hash)
        newPath = addLanguageToPath(currentPath, newLanguage)
      }
    } else {
      // Not a game page, use normal language switching
      const currentPath = removeLanguageFromPath(location.pathname + location.search + location.hash)
      newPath = addLanguageToPath(currentPath, newLanguage)
    }

    setLanguageState(newLanguage)
    navigate(newPath, { replace: true })
  }

  const currentTranslations = getTranslations(language)

  const t = (key: string, params?: Record<string, any>) => {
    return translate(currentTranslations, key, params)
  }

  return (
    <TranslationContext.Provider
      value={{
        language,
        setLanguage: setLanguageWithNavigation,
        t,
        navigate: localizedNavigate,
      }}
    >
      {children}
    </TranslationContext.Provider>
  )
} 